import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface CartItem {
  productId: string;
  name: string;
  price: number;
  unit: string;
  image: string;
  quantity: number;
  stock: number;
  model: string;
  specId?: string;
  specName?: string;
  remark?: string;
}

export interface CartActionOptions {
  /** Anchor element for stock tip near the clicked button */
  anchor?: Element | EventTarget | null;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity' | 'remark'>, options?: CartActionOptions) => void;
  removeItem: (productId: string) => void;
  removeItems: (productIds: string[]) => void;
  /** 按商品+规格移除购物车行 */
  removeLines: (lines: Array<{ productId: string; specId?: string }>) => void;
  updateQuantity: (productId: string, quantity: number, options?: CartActionOptions) => void;
  updateRemark: (productId: string, remark: string) => void;
  clearCart: () => void;
  totalCount: number;
  totalAmount: number;
}

const CartContext = createContext<CartContextType | null>(null);
const CART_STORAGE_KEY = 'cart';

/** stock < 0 means unlimited; only validate when stock >= 0 */
function shouldValidateStock(stock: number) {
  return Number.isFinite(stock) && stock >= 0;
}

function tipStockInsufficient(anchor?: Element | EventTarget | null) {
  void import('@/utils/message').then(({ toastWarning }) => {
    toastWarning('库存不足', anchor);
  });
}

/** Returns null when item should be removed from cart */
function normalizeQuantity(quantity: number, stock: number): number | null {
  const qty = Math.floor(Number(quantity));
  if (!Number.isFinite(qty) || qty < 1) return null;
  if (!shouldValidateStock(stock)) return qty;
  const max = Math.floor(stock);
  if (max < 1) return null;
  return Math.min(qty, max);
}

function persistCart(items: CartItem[]) {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
}

function sanitizeCartItems(raw: unknown): CartItem[] {
  if (!Array.isArray(raw)) return [];
  const cleaned: CartItem[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as CartItem;
    if (!item.productId) continue;
    const qty = normalizeQuantity(item.quantity, Number(item.stock));
    if (qty == null) continue;
    cleaned.push({
      ...item,
      quantity: qty,
      stock: Number(item.stock),
      remark: item.remark || '',
    });
  }
  return cleaned;
}

function sameLine(a: CartItem, productId: string, specId?: string) {
  return a.productId === productId && (a.specId || '') === (specId || '');
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      const cleaned = sanitizeCartItems(saved ? JSON.parse(saved) : []);
      persistCart(cleaned);
      return cleaned;
    } catch {
      return [];
    }
  });

  const addItem = useCallback(
    (item: Omit<CartItem, 'quantity' | 'remark'>, options?: CartActionOptions) => {
      const anchor = options?.anchor;
      setItems((prev) => {
        const stock = Number(item.stock);
        const existing = prev.find((i) => sameLine(i, item.productId, item.specId));

        if (existing) {
          const current = existing.quantity >= 1 ? existing.quantity : 0;
          if (shouldValidateStock(stock) && current >= stock) {
            tipStockInsufficient(anchor);
            return prev;
          }

          const nextQty = normalizeQuantity(current + 1, stock);
          if (nextQty == null) {
            tipStockInsufficient(anchor);
            if (existing.quantity < 1) {
              const next = prev.filter((i) => !sameLine(i, item.productId, item.specId));
              persistCart(next);
              return next;
            }
            return prev;
          }
          if (nextQty === current) {
            tipStockInsufficient(anchor);
            return prev;
          }

          const next = prev.map((i) =>
            sameLine(i, item.productId, item.specId)
              ? { ...i, ...item, quantity: nextQty, stock }
              : i,
          );
          persistCart(next);
          return next;
        }

        const nextQty = normalizeQuantity(1, stock);
        if (nextQty == null) {
          tipStockInsufficient(anchor);
          return prev;
        }
        const next = [...prev, { ...item, stock, quantity: nextQty, remark: '' }];
        persistCart(next);
        return next;
      });
    },
    [],
  );

  const removeItem = useCallback((productId: string) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.productId !== productId);
      persistCart(next);
      return next;
    });
  }, []);

  const removeItems = useCallback((productIds: string[]) => {
    const idSet = new Set(productIds.map(String));
    setItems((prev) => {
      const next = prev.filter((i) => !idSet.has(String(i.productId)));
      persistCart(next);
      return next;
    });
  }, []);

  const removeLines = useCallback((lines: Array<{ productId: string; specId?: string }>) => {
    const keys = new Set(lines.map((l) => `${l.productId}::${l.specId || ''}`));
    setItems((prev) => {
      const next = prev.filter((i) => !keys.has(`${i.productId}::${i.specId || ''}`));
      persistCart(next);
      return next;
    });
  }, []);

  const updateQuantity = useCallback(
    (productId: string, quantity: number, options?: CartActionOptions) => {
      const anchor = options?.anchor;
      setItems((prev) => {
        const target = prev.find((i) => i.productId === productId);
        if (!target) return prev;

        const stock = Number(target.stock);
        const requested = Math.floor(Number(quantity));
        if (
          shouldValidateStock(stock) &&
          Number.isFinite(requested) &&
          requested >= 1 &&
          requested > stock
        ) {
          tipStockInsufficient(anchor);
        }

        const nextQty = normalizeQuantity(quantity, stock);
        const next =
          nextQty == null
            ? prev.filter((i) => i.productId !== productId)
            : prev.map((i) => (i.productId === productId ? { ...i, quantity: nextQty } : i));
        persistCart(next);
        return next;
      });
    },
    [],
  );

  const updateRemark = useCallback((productId: string, remark: string) => {
    setItems((prev) => {
      const next = prev.map((i) => (i.productId === productId ? { ...i, remark } : i));
      persistCart(next);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  }, []);

  const totalCount = useMemo(
    () => items.reduce((sum, i) => sum + (i.quantity > 0 ? i.quantity : 0), 0),
    [items],
  );
  const totalAmount = useMemo(
    () => items.reduce((sum, i) => sum + (i.quantity > 0 ? i.price * i.quantity : 0), 0),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      removeItems,
      removeLines,
      updateQuantity,
      updateRemark,
      clearCart,
      totalCount,
      totalAmount,
    }),
    [
      items,
      addItem,
      removeItem,
      removeItems,
      removeLines,
      updateQuantity,
      updateRemark,
      clearCart,
      totalCount,
      totalAmount,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
